import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft, Save } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { LoginGate } from "@/components/LoginGate";
import { CompanyForm, useCompanyForm } from "@/components/clients/CompanyForm";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { StatusBanner } from "@/components/ui/status-banner";
import { supabase } from "@/lib/supabase";

export const Route = createFileRoute("/clients_/new")({
  component: NewClientPage,
});

function NewClientPage() {
  return (
    <LoginGate title="DeepDive" subtitle="Iniciá sesión para crear clientes.">
      {({ user, signOut }) => (
        <AppShell user={user} onLogout={signOut}>
          <NewClientForm />
        </AppShell>
      )}
    </LoginGate>
  );
}

function NewClientForm() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useCompanyForm();
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!form.name.trim()) throw new Error("El nombre es requerido.");
      const { data, error } = await supabase
        .from("companies")
        .insert({
          name: form.name.trim(),
          razon_social: form.razon_social.trim() || null,
          email: form.email.trim().toLowerCase() || null,
          phone: form.phone.trim() || null,
          rubro: form.rubro.trim() || null,
          is_active: form.is_active,
          notes: form.notes.trim() || null,
        })
        .select("id")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["companies"] });
      navigate({
        to: "/clients/$clientId",
        params: { clientId: res.id },
      });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="px-6 py-5">
      <Link
        to="/clients"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-4"
      >
        <ArrowLeft className="h-3 w-3" />
        Clientes
      </Link>

      <h1 className="text-2xl font-semibold tracking-tight mb-2">
        Nuevo cliente
      </h1>
      <p className="text-muted-foreground text-sm mb-8">
        Cargá la información básica. Las búsquedas las agregás después dentro
        del cliente.
      </p>

      {error && (
        <div className="mb-6">
          <StatusBanner
            variant="error"
            title="No se pudo crear el cliente"
            onDismiss={() => setError(null)}
          >
            {error}
          </StatusBanner>
        </div>
      )}

      <Card className="p-6">
        <CompanyForm value={form} onChange={setForm} />

        <div className="flex justify-end gap-2 mt-6 pt-5 border-t border-border">
          <Button variant="ghost" asChild>
            <Link to="/clients">Cancelar</Link>
          </Button>
          <Button
            variant="primary"
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
            disabled={!form.name.trim()}
          >
            <Save className="h-4 w-4" />
            Crear cliente
          </Button>
        </div>
      </Card>
    </div>
  );
}
